// src/TopicManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

function TopicManagement() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();

  // --- (States ที่ถูกต้อง) ---
  const [topics, setTopics] = useState([]); 
  const [parsers, setParsers] = useState([]); 
  const [optionsData, setOptionsData] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);

  // --- States สำหรับฟอร์ม ---
  const [editingId, setEditingId] = useState(null); 
  const [topicName, setTopicName] = useState('');
  const [topicUrl, setTopicUrl] = useState('');
  const [selectedMinistryId, setSelectedMinistryId] = useState('');
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [selectedParser, setSelectedParser] = useState('');

  // --- States สำหรับ Modals ---
  const [isDeleteTopicModalOpen, setIsDeleteTopicModalOpen] = useState(false);
  const [deletingTopic, setDeletingTopic] = useState(null);

  // --- (fetchData ที่ถูกต้อง) ---
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const [topicsRes, optionsRes, parsersRes] = await Promise.all([
        axios.get(`${API_URL}/topics`, authHeaders),
        axios.get(`${API_URL}/feed-options-hierarchical`, authHeaders),
        axios.get(`${API_URL}/parser-options`, authHeaders)
      ]);
      
      setTopics(topicsRes.data);
      setOptionsData(optionsRes.data); 
      setParsers(parsersRes.data);
      
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูล Feed ได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // (แก้ Dep Array ที่ซ้ำซ้อน)

  // (สกัด Dropdown "หน่วยงาน" จาก "กระทรวง" ที่เลือก)
  const agenciesForSelectedMinistry = optionsData.find(
    (m) => m.id === parseInt(selectedMinistryId)
  )?.agencies || [];

  // (รวม clearForm เป็นฟังก์ชันเดียว)
  const clearForm = () => {
    setEditingId(null);
    setTopicName('');
    setTopicUrl('');
    setSelectedMinistryId('');
    setSelectedAgencyId('');
    setSelectedParser('');
  };

  // --- (ฟังก์ชันจัดการ Topic (CRUD)) ---
  const handleTopicSubmit = async (e) => {
    e.preventDefault();
    if (!topicName || !topicUrl || !selectedAgencyId || !selectedParser) {
      return toast.error("กรุณากรอกข้อมูลหัวข้อให้ครบ (ชื่อ, URL, หน่วยงาน, สูตร)");
    }
    
    const payload = { 
      name: topicName, 
      url: topicUrl, 
      agency_id: parseInt(selectedAgencyId), 
      parser_type: selectedParser 
    };

    const method = editingId ? 'put' : 'post';
    const url = editingId 
      ? `${API_URL}/topics/${editingId}` 
      : `${API_URL}/topics`;

    try {
      const res = await axios({ method, url, data: payload, ...getAuthHeaders() });
      toast.success(res.data.msg);
      clearForm(); 
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };

  const handleTopicEditClick = (topic) => {
    setEditingId(topic.id); 
    setTopicName(topic.name);
    setTopicUrl(topic.url);
    setSelectedMinistryId(topic.ministry_id);
    setSelectedAgencyId(topic.agency_id);
    setSelectedParser(topic.parser_type);
  };

  const handleOpenDeleteTopicModal = (topic) => {
    setDeletingTopic(topic);
    setIsDeleteTopicModalOpen(true);
  };

  // --- (แก้ไข) Syntax Error ที่นี่ ---
  const handleConfirmDeleteTopic = async () => {
    if (!deletingTopic) return;
    try {
      const res = await axios.delete(`${API_URL}/topics/${deletingTopic.id}`, getAuthHeaders());
      toast.success(res.data.msg);
      clearForm(); 
      fetchData();
    } catch (err) { // (เพิ่มวงเล็บปีกกา)
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteTopicModalOpen(false);
      setDeletingTopic(null);
    }
  };
  // ------------------------------

  // --- (Render) ---
  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return <div className="container mx-auto p-8 text-center">Loading...</div>;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin/feeds')}
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          &larr; ย้อนกลับ
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* (ฟอร์ม Topic) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-xl p-8 sticky top-28">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                {editingId ? 'แก้ไขหัวข้อ' : 'เพิ่มหัวข้อใหม่'}
              </h2>
              
              <form onSubmit={handleTopicSubmit} className="space-y-4">
                
                <div>
                  <label htmlFor="topicName" className="block text-base font-medium text-gray-700">ชื่อหัวข้อ (เช่น "ประกาศกรม") *</label>
                  <input
                    type="text" id="topicName" value={topicName}
                    onChange={(e) => setTopicName(e.target.value)} required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                <div>
                  <label htmlFor="topicUrl" className="block text-base font-medium text-gray-700">URL (ลิงก์หน้าตาราง) *</label>
                  <input
                    type="url" id="topicUrl" value={topicUrl}
                    onChange={(e) => setTopicUrl(e.target.value)} required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
                    placeholder="https://www.customs.go.th/..."
                  />
                </div>
                
                {/* Dropdown 3 ระดับ */}
                <div>
                  <label htmlFor="ministry" className="block text-base font-medium text-gray-700">สังกัดกระทรวง *</label>
                  <select
                    id="ministry"
                    value={selectedMinistryId}
                    onChange={(e) => {
                      setSelectedMinistryId(e.target.value);
                      setSelectedAgencyId(''); 
                    }}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm bg-white"
                  >
                    <option value="">-- เลือกกระทรวง --</option>
                    {optionsData.map((min) => (
                      <option key={min.id} value={min.id}>{min.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="agency" className="block text-base font-medium text-gray-700">สังกัดหน่วยงาน *</label>
                  <select
                    id="agency"
                    value={selectedAgencyId}
                    onChange={(e) => setSelectedAgencyId(e.target.value)}
                    required
                    disabled={!selectedMinistryId || agenciesForSelectedMinistry.length === 0} 
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm bg-white disabled:bg-gray-100"
                  >
                    <option value="">-- เลือกหน่วยงาน --</option>
                    {agenciesForSelectedMinistry.map((agency) => (
                      <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* 4. เลือกสูตร Parser */}
                <div>
                  <label htmlFor="parser" className="block text-base font-medium text-gray-700">เลือกสูตร Parser *</label>
                  <select
                    id="parser" value={selectedParser}
                    onChange={(e) => setSelectedParser(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm bg-white"
                  >
                    <option value="">-- เลือกสูตร --</option>
                    {parsers.map((parser) => (
                      <option key={parser.key} value={parser.key}>{parser.name}</option>
                    ))}
                  </select>
                </div>
                
                <button
                  type="submit"
                  className={`w-full px-4 py-3 font-semibold text-xl text-white rounded-md ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {editingId ? 'บันทึกการแก้ไข' : 'เพิ่มหัวข้อ'}
                </button>
                {editingId && (
                  <button type="button" onClick={clearForm} className="w-full mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
                    ยกเลิก
                  </button>
                )}
              </form> 
            </div>
          </div>

          {/* (ตาราง Topic) */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">หัวข้อที่มีอยู่</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">หัวข้อ</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">หน่วยงาน</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">กระทรวง</th>
                    <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topics.map((topic) => (
                    <tr key={topic.id}>
                      <td className="px-6 py-4 text-lg font-medium">{topic.name}</td>
                      <td className="px-6 py-4 text-lg">{topic.agency_name}</td>
                      <td className="px-6 py-4 text-lg text-gray-600">{topic.ministry_name}</td>
                      <td className="px-6 py-4 text-right space-x-4">
                        <button onClick={() => handleTopicEditClick(topic)} className="text-blue-600 hover:text-blue-900">แก้ไข</button>
                        <button onClick={() => handleOpenDeleteTopicModal(topic)} className="text-red-600 hover:text-red-900">ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* (Modal ลบ Topic) */}
      {isDeleteTopicModalOpen && deletingTopic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">ยืนยันการลบหัวข้อ</h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบ: <strong className="text-red-600">{deletingTopic.name}</strong>?
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteTopicModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteTopic}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TopicManagement;